import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, DestroyRef, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';

import type { CentralTransferenciaDto, MedicoValidacaoTransferenciaDto, TipoSolicitacaoTransferencia } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.model';
import { CentralTransferenciaService } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.service';
import { AuthService } from '@/core/auth/auth.service';
import { Empty } from '@/shared/components/layout/empty/empty';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { DateUtils } from '@/shared/utils/date-utils';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PessoaService } from '@/shared/services/pessoa.service';

interface TipoSolicitacaoOpcao {
    label: string;
    value: TipoSolicitacaoTransferencia;
}

@Component({
    selector: 'app-solicitar-transferencia',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, Button, SelectEstado, CardContent, Empty, CanDirective],
    templateUrl: './solicitar-transferencia.html',
    styleUrl: './solicitar-transferencia.scss'
})
/** Central de transferência: solicitação de transferência/inscrição secundária entre UFs. */
export class SolicitarTransferenciaComponent implements OnInit {
    protected readonly roleCentralSolicitar = PERMISSOES.medicos.centralTransferencia.solicitar;
    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly centralTransferenciaService = inject(CentralTransferenciaService);
    private readonly messageService = inject(MessageService);
    private readonly pessoaService = inject(PessoaService);
    private crmOrigem = '';
    private ufOrigem = '';
    private ufLogada = '';

    readonly todasUfs = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
    ufsDestinoPermitidas: string[] = [...this.todasUfs];

    readonly formSolicitacao = this.fb.nonNullable.group({
        ufDestino: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
        tipoSolicitacao: this.fb.nonNullable.control<'' | TipoSolicitacaoTransferencia>('', Validators.required)
    });

    carregandoValidacao = signal(false);
    carregandoSolicitacao = signal(false);
    bloquearSolicitacaoPorUf = false;
    validacao: MedicoValidacaoTransferenciaDto | null = null;
    tiposSolicitacaoDisponiveis: TipoSolicitacaoOpcao[] = [];
    retornoSolicitacao: CentralTransferenciaDto | null = null;
    readonly selecionarRegistro = output<void>();
    readonly abrirPesquisaRegistro = () => this.selecionarRegistro.emit();

    get registroNaoSelecionado(): boolean {
        return !this.crmOrigem || !this.ufOrigem;
    }

    ngOnInit(): void {
        // Acompanha a pessoa em sessão em vez de ler um retrato dela.
        //
        // Lendo só aqui, quem pesquisava o médico COM esta tela já aberta continuava vendo "Registro
        // não selecionado": o dado chegava ao serviço, mas ninguém relia. `pessoaAtual$` é
        // BehaviorSubject, então a primeira emissão é imediata e cobre também a tela aberta já com o
        // médico escolhido.
        this.pessoaService.pessoaAtual$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.aplicarPessoaSelecionada());

        this.formSolicitacao.controls.ufDestino.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.retornoSolicitacao = null;
        });
    }

    /**
     * Relê a pessoa em sessão e revalida a transferência quando a origem muda.
     *
     * A revalidação é condicionada porque ela consulta a API: emissão que não muda CRM nem UF —
     * outro campo da pessoa, ou a mesma pessoa reemitida — não precisa de uma segunda chamada.
     */
    private aplicarPessoaSelecionada(): void {
        const origemAnterior = `${this.crmOrigem}|${this.ufOrigem}`;
        this.preencherDadosIniciais();

        if (`${this.crmOrigem}|${this.ufOrigem}` !== origemAnterior) {
            this.validarTransferencia();
        }
    }

    validarTransferencia(): void {
        this.retornoSolicitacao = null;
        this.validacao = null;
        this.tiposSolicitacaoDisponiveis = [];
        this.formSolicitacao.controls.tipoSolicitacao.setValue('', { emitEvent: false });

        if (this.registroNaoSelecionado) {
            return;
        }
        this.carregandoValidacao.set(true);

        this.centralTransferenciaService
            .validarTransferencia(this.crmOrigem, this.ufOrigem)
            .pipe(finalize(() => this.carregandoValidacao.set(false)))
            .subscribe({
                next: (response) => {
                    this.validacao = {
                        podeTransferir: !!response?.podeTransferir,
                        podeSecundaria: !!response?.podeSecundaria,
                        impedimentos: response?.impedimentos ?? []
                    };

                    this.tiposSolicitacaoDisponiveis = this.montarTiposDisponiveis(this.validacao);
                    const tipoInicial = this.tiposSolicitacaoDisponiveis[0]?.value ?? '';
                    this.formSolicitacao.controls.tipoSolicitacao.setValue(tipoInicial, { emitEvent: false });
                },
                error: (erro) => {
                    this.notificarErro(this.extrairMensagemErro(erro) ?? 'Não foi possível validar a transferência.');
                }
            });
    }

    solicitar(): void {
        this.retornoSolicitacao = null;

        if (this.bloquearSolicitacaoPorUf) {
            this.notificarErro('Solicitação bloqueada: a UF da pessoa pesquisada e diferente da UF do usuário logado.');
            return;
        }

        if (!this.validacao) {
            this.notificarErro('Realize a validação antes de solicitar.');
            return;
        }

        const { tipoSolicitacao } = this.formSolicitacao.getRawValue();
        if (!tipoSolicitacao) {
            this.formSolicitacao.controls.tipoSolicitacao.markAsTouched();
            this.notificarErro('Selecione o tipo de solicitação.');
            return;
        }

        if (this.registroNaoSelecionado) {
            return;
        }

        const ufDestino = this.normalizarUf(this.formSolicitacao.controls.ufDestino.value);

        if (!ufDestino) {
            this.notificarErro('Selecione a UF de destino.');
            return;
        }

        this.carregandoSolicitacao.set(true);
        this.centralTransferenciaService
            .solicitar(this.crmOrigem, this.ufOrigem, ufDestino, tipoSolicitacao)
            .pipe(finalize(() => this.carregandoSolicitacao.set(false)))
            .subscribe({
                next: (response) => {
                    this.retornoSolicitacao = response;
                },
                error: (erro) => {
                    this.notificarErro(this.extrairMensagemErro(erro) ?? 'Não foi possível concluir a solicitação.');
                }
            });
    }

    formatarData(valor?: string | null): string {
        const data = (valor ?? '').trim();
        if (!data) {
            return '-';
        }

        if (DateUtils.isDateString(data)) {
            return DateUtils.formatDate(data);
        }

        return data;
    }

    obterDescricaoTipo(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoTipoSolicitacao) || this.valorTexto(item.tipoSolicitacao) || '-';
    }

    obterDescricaoStatus(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoStatus) || this.valorTexto(item.status) || '-';
    }

    formatarUf(uf?: string | null): string {
        const sigla = this.normalizarUf(uf ?? '');
        return sigla || '-';
    }

    obterBandeiraUf(uf?: string | null): string | null {
        const sigla = this.normalizarUf(uf ?? '');
        if (!sigla) {
            return null;
        }
        return `images/estados/${sigla}.png`;
    }

    private preencherDadosIniciais(): void {
        const ufLogada = this.normalizarUf(this.authService.ufDoUsuario() ?? '');
        this.ufLogada = ufLogada;
        const pessoaSessao = this.obterPessoaSessao();
        this.crmOrigem = this.normalizarCrm(pessoaSessao?.crm ?? '');
        this.ufOrigem = this.normalizarUf(pessoaSessao?.uf ?? '');
        this.bloquearSolicitacaoPorUf = !!this.ufLogada && !!this.ufOrigem && this.ufLogada !== 'BR' && this.ufLogada !== this.ufOrigem;
        this.ufsDestinoPermitidas = this.todasUfs.filter((uf) => uf !== ufLogada);

        let ufDestinoInicial = this.normalizarUf(this.formSolicitacao.controls.ufDestino.value);
        if (!ufDestinoInicial || !this.ufsDestinoPermitidas.includes(ufDestinoInicial)) {
            ufDestinoInicial = this.ufsDestinoPermitidas[0] ?? '';
        }

        this.formSolicitacao.patchValue(
            {
                ufDestino: ufDestinoInicial
            },
            { emitEvent: false }
        );
    }

    onUfDestinoChange(uf: string): void {
        this.formSolicitacao.controls.ufDestino.setValue(this.normalizarUf(uf));
    }

    selecionarTipoSolicitacao(tipo: TipoSolicitacaoTransferencia): void {
        this.formSolicitacao.controls.tipoSolicitacao.setValue(tipo);
    }

    isTipoSolicitacaoSelecionado(tipo: TipoSolicitacaoTransferencia): boolean {
        return this.formSolicitacao.controls.tipoSolicitacao.value === tipo;
    }

    obterDescricaoTipoSolicitacao(tipo: TipoSolicitacaoTransferencia): string {
        if (tipo === 'S') {
            return 'Solicitação de inscrição secundária no CRM de destino.';
        }
        return 'Solicitação de transferência entre CRM de origem e destino.';
    }

    private montarTiposDisponiveis(validacao: MedicoValidacaoTransferenciaDto): TipoSolicitacaoOpcao[] {
        const tipos: TipoSolicitacaoOpcao[] = [];

        if (validacao.podeTransferir) {
            tipos.push({ label: 'Transferência', value: 'T' });
        }

        if (validacao.podeSecundaria) {
            tipos.push({ label: 'Inscrição Secundária', value: 'S' });
        }

        return tipos;
    }

    private obterPessoaSessao(): { crm?: string; registro?: string; uf?: string } | null {
        return this.pessoaService.getPessoa() ?? this.pessoaService.getPessoaReference();
    }

    private normalizarCrm(valor: unknown): string {
        return (valor ?? '').toString().trim().replace(/\D+/g, '');
    }

    private normalizarUf(valor: unknown): string {
        return (valor ?? '')
            .toString()
            .trim()
            .toUpperCase()
            .replace(/[^A-Z]/g, '')
            .slice(0, 2);
    }

    private valorTexto(valor: unknown): string {
        return (valor ?? '').toString().trim();
    }

    private extrairMensagemErro(erro: unknown): string | undefined {
        const e = erro as { error?: { message?: string; detail?: string; error?: string }; message?: string; statusText?: string };
        return e?.error?.message || e?.error?.detail || e?.error?.error || e?.message || e?.statusText;
    }

    private notificarErro(detail: string): void {
        this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail
        });
    }
}
